import { WAV_RATE, concatSamples, downsample, encodeWav } from './wav';

/** The longest question recorded: 60 s of 16 kHz PCM16 is just under the 2 MB upload limit. */
export const MAX_RECORDING_MS = 60_000;

export interface Recording {
  /** Stops and returns the WAV. */
  stop(): Promise<Blob>;
  /** Stops and throws the audio away. */
  cancel(): void;
}

type AudioContextClass = typeof AudioContext;

function audioContextClass(): AudioContextClass | undefined {
  const w = window as { AudioContext?: AudioContextClass; webkitAudioContext?: AudioContextClass };
  return w.AudioContext ?? w.webkitAudioContext;
}

/** Whether this browser can record at all; the panel hides the mic when it cannot. */
export function canRecord(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function' &&
    typeof window !== 'undefined' &&
    audioContextClass() !== undefined
  );
}

/**
 * Starts recording from the microphone. Samples are collected raw through a
 * ScriptProcessor (an AudioWorklet would need a separate module file for a
 * minute of speech) and encoded only on stop. `onLimit` fires when the minute
 * is up, so the caller can stop as if the button were pressed.
 */
export async function startRecording(onLimit: () => void): Promise<Recording> {
  const Context = audioContextClass();
  if (Context === undefined) throw new Error('audio recording is not supported');
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
  });
  const context = new Context();
  const source = context.createMediaStreamSource(stream);
  const processor = context.createScriptProcessor(4096, 1, 1);
  const chunks: Float32Array[] = [];
  processor.onaudioprocess = (event) => {
    chunks.push(event.inputBuffer.getChannelData(0).slice());
  };
  source.connect(processor);
  processor.connect(context.destination);
  const limit = setTimeout(onLimit, MAX_RECORDING_MS);

  let closed = false;
  const close = async (): Promise<void> => {
    if (closed) return;
    closed = true;
    clearTimeout(limit);
    processor.disconnect();
    source.disconnect();
    for (const track of stream.getTracks()) track.stop();
    await context.close();
  };

  return {
    async stop() {
      await close();
      const samples = downsample(concatSamples(chunks), context.sampleRate, WAV_RATE);
      const cap = (WAV_RATE * MAX_RECORDING_MS) / 1000;
      const wav = encodeWav(samples.subarray(0, cap), WAV_RATE);
      return new Blob([wav], { type: 'audio/wav' });
    },
    cancel() {
      void close();
    },
  };
}
